import { useEffect, useRef } from 'react';
import type { BrushPoint, StudioConfig } from '../types';

interface EraseCanvasProps {
  source: string;
  config: StudioConfig;
  point: BrushPoint | null;
}

function drawFittedImage(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number,
  fit: StudioConfig['fit'],
  scaleMultiplier: number,
  focalX: number,
  focalY: number,
) {
  const baseScale = fit === 'cover'
    ? Math.max(width / image.naturalWidth, height / image.naturalHeight)
    : Math.min(width / image.naturalWidth, height / image.naturalHeight);
  const scale = baseScale * scaleMultiplier;
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;
  const x = (width - drawWidth) * (focalX / 100);
  const y = (height - drawHeight) * (focalY / 100);
  context.drawImage(image, x, y, drawWidth, drawHeight);
}

export function EraseCanvas({ source, config, point }: EraseCanvasProps) {
  const visibleRef = useRef<HTMLCanvasElement>(null);
  const maskRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const dirtyRef = useRef(true);
  const configRef = useRef(config);
  configRef.current = config;

  useEffect(() => {
    const image = new Image();
    image.decoding = 'async';
    image.src = source;
    image.onload = () => {
      imageRef.current = image;
      dirtyRef.current = true;
    };
    return () => {
      image.onload = null;
    };
  }, [source]);

  useEffect(() => {
    const visible = visibleRef.current;
    if (!visible) return;
    const mask = document.createElement('canvas');
    maskRef.current = mask;

    const resize = () => {
      const rect = visible.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      visible.width = Math.max(1, Math.round(rect.width * ratio));
      visible.height = Math.max(1, Math.round(rect.height * ratio));
      mask.width = visible.width;
      mask.height = visible.height;
      dirtyRef.current = true;
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(visible);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!point || !maskRef.current || !visibleRef.current) return;
    const mask = maskRef.current;
    const visible = visibleRef.current;
    const context = mask.getContext('2d');
    if (!context) return;
    const ratioX = mask.width / Math.max(1, visible.clientWidth);
    const ratioY = mask.height / Math.max(1, visible.clientHeight);
    const radius = config.brushSize * 0.5 * ratioX;
    const inner = radius * (1 - config.brushFeather / 100);
    const x = point.x * ratioX;
    const y = point.y * ratioY;
    const gradient = context.createRadialGradient(x, y, Math.max(0, inner), x, y, radius);
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    context.globalCompositeOperation = 'source-over';
    context.fillStyle = gradient;
    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2);
    context.fill();
    dirtyRef.current = true;
  }, [point, config.brushSize, config.brushFeather]);

  useEffect(() => {
    let frame = 0;
    let previous = performance.now();
    const draw = (now: number) => {
      const visible = visibleRef.current;
      const mask = maskRef.current;
      const image = imageRef.current;
      const settings = configRef.current;
      if (visible && mask && image?.complete) {
        const maskContext = mask.getContext('2d');
        const context = visible.getContext('2d');
        const elapsed = Math.min(40, now - previous);
        previous = now;
        if (!settings.erasePersist && settings.autoRecover > 0 && maskContext) {
          maskContext.globalCompositeOperation = 'destination-out';
          maskContext.fillStyle = `rgba(0,0,0,${Math.min(0.16, elapsed * settings.autoRecover / 85000)})`;
          maskContext.fillRect(0, 0, mask.width, mask.height);
          dirtyRef.current = true;
        }
        if (context && dirtyRef.current) {
          context.clearRect(0, 0, visible.width, visible.height);
          context.save();
          context.filter = `brightness(${settings.brightness}%) contrast(${settings.contrast}%) saturate(${settings.saturation}%) blur(${settings.blur}px)`;
          drawFittedImage(
            context,
            image,
            visible.width,
            visible.height,
            settings.fit,
            settings.imageBScale,
            settings.imageBFocalX,
            settings.imageBFocalY,
          );
          context.globalCompositeOperation = 'destination-in';
          context.drawImage(mask, 0, 0);
          context.restore();
          dirtyRef.current = false;
        }
      }
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    dirtyRef.current = true;
  }, [config]);

  return <canvas ref={visibleRef} className="erase-canvas" aria-hidden="true" />;
}
